import { API_BASE_URL } from "@/shared/config/env";
import { useAuthStore } from "@/shared/auth/authStore";
import { http } from "./httpClient";
import type { IceServers, Macros, Queue } from "./types";

/**
 * Contrato do softphone legado (`PhoneStatusController` `/rest/status/**`, `BreakController`
 * `/breaks/**`, `LogController` `/rest/log/**`). Todas as chamadas pulam o loading global
 * (`X-Skip-Global-Loading`) porque rodam em segundo plano (heartbeat, polling de aprovação).
 */

function apiUrl(path: string) {
  return `${API_BASE_URL}${path}`;
}

const BG = { "X-Skip-Global-Loading": "1" } as const;

/* ---------------------------------------------------------------- tipos -- */

export type SessionStatus = "OFFLINE" | "ONLINE" | "PAUSED";
export type PhoneStatus = "OFFLINE" | "ONLINE" | "RINGING" | "DIALING" | "INUSE";

/** `pojo/PhoneInfo` (`PhoneInfoDTOImpl.build`). Campos nulos são omitidos pelo backend. */
export type PhoneInfo = {
  phoneNumber: string;
  phoneNumberAlt?: string | null;
  phonePassword?: string | null;
  wsServer?: string | null;
  sipServer?: string | null;
  automaticAnswering?: boolean;
  iceServers?: IceServers | null;
  macros?: (Macros & { layout?: string | null }) | null;
  breaks?: Macros | null;
  sessionStatus?: SessionStatus | null;
  sessionInfo?: string | null;
  sessionStatusBegin?: string | null;
  phoneStatus?: PhoneStatus | null;
  info?: string | null;
  callid?: string | null;
  sysid?: string | null;
  phoneStatusBegin?: string | null;
  statusUpdate?: string | null;
  queues?: Queue[] | null;
};

/** `dto/response/BreakStatusDto` — status calculado no construtor (limite/aprovação do dia). */
export type BreakStatus = "ENABLED" | "NEED_APPROVE" | "DISABLED";

export type BreakStatusDto = {
  title?: string | null;
  value?: string | null;
  breakId?: number | null;
  maxQuantity?: number | null;
  supervisorApproval?: boolean | null;
  breakApprovalId?: number | null;
  approved?: boolean | null;
  countPause?: number | null;
  status?: BreakStatus | null;
};

export type BreakApprovalResponse = {
  id?: number;
  approve?: boolean | null;
  [key: string]: unknown;
};

/** `pojo/Payload` (`PayloadDTOImpl`). */
export type CallPayload = { id?: number | string; key?: string; value?: string; [key: string]: unknown };

/** `dto/response/QueueResponseDto`. */
export type TransferredFrom = { queueNumber?: string | null; name?: string | null; description?: string | null };

export type HeartbeatPayload = {
  sessionStatus: SessionStatus;
  sessionInfo?: string | null;
  phoneStatus: PhoneStatus;
  phoneInfo?: string | null;
  callid?: string | null;
  sysid?: string | null;
  softphonecallid?: string | null;
};

function heartbeatForm(p: HeartbeatPayload): FormData {
  // Mesmo formato do softphone legado: multipart, campos opcionais só quando não nulos.
  const f = new FormData();
  f.append("sessionStatus", p.sessionStatus);
  if (p.sessionInfo != null) f.append("sessionInfo", p.sessionInfo);
  f.append("phoneStatus", p.phoneStatus);
  if (p.phoneInfo != null) f.append("phoneInfo", p.phoneInfo);
  if (p.callid != null) f.append("callid", p.callid);
  if (p.sysid != null) f.append("sysid", p.sysid);
  if (p.softphonecallid != null) f.append("softphonecallid", p.softphonecallid);
  return f;
}

/** "yyyy-MM-dd HH:mm:ss" no fuso de operação (formato de `getTransferredFrom.callStartedAt`). */
function formatBackendDateTime(d: Date, timeZone = "America/Sao_Paulo"): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
  const hour = get("hour") === "24" ? "00" : get("hour");
  return `${get("year")}-${get("month")}-${get("day")} ${hour}:${get("minute")}:${get("second")}`;
}

/* ------------------------------------------------------------ endpoints -- */

export const softphoneApi = {
  /** 404 quando o usuário não tem ramal associado. */
  async phoneByLogin(): Promise<PhoneInfo | null> {
    try {
      const { data } = await http.get<PhoneInfo>(apiUrl("/rest/status/phoneByLogin"), { headers: BG });
      return data ?? null;
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status === 404) return null;
      throw e;
    }
  },

  async heartbeat(p: HeartbeatPayload): Promise<void> {
    await http.post(apiUrl("/rest/status/heartbeat"), heartbeatForm(p), { headers: BG });
  },

  /**
   * Heartbeat "melhor esforço" no fechamento da aba. `sendBeacon` não envia o header
   * Authorization, então usamos `fetch(..., { keepalive: true })`, que sobrevive ao unload.
   */
  heartbeatKeepalive(p: HeartbeatPayload): void {
    const token = useAuthStore.getState().token;
    if (!token) return;
    try {
      void fetch(apiUrl("/rest/status/heartbeat"), {
        method: "POST",
        body: heartbeatForm(p),
        headers: { Authorization: `Bearer ${token}` },
        keepalive: true,
        credentials: "same-origin",
      }).catch(() => {});
    } catch {
      /* navegador sem keepalive: o janitor do backend marca "Connection lost" em ~60s */
    }
  },

  /** Pausas do usuário com contagem/limite/aprovação do dia. Exige ROLE_USER. */
  async findBreakByUser(): Promise<BreakStatusDto[]> {
    const { data } = await http.get<BreakStatusDto[]>(apiUrl("/breaks/findBreakByUser"), { headers: BG });
    return (Array.isArray(data) ? data : []).filter((b) => b && b.breakId != null);
  },

  async createBreakApproval(idBreak: number): Promise<BreakApprovalResponse> {
    const { data } = await http.post<BreakApprovalResponse>(apiUrl("/breaks/createBreakApproval"), null, {
      params: { idBreak },
      headers: BG,
    });
    return data;
  },

  /**
   * Presença no ServiceNow (0 = "Disponível"). Pode responder 400 quando a integração
   * não está configurada e 422 quando a pausa exige aprovação — o chamador trata.
   */
  async changeStatusPhone(breakId: number): Promise<void> {
    await http.put(apiUrl(`/rest/status/change/changeStatusPhone/${breakId}`), null, { headers: BG });
  },

  async listPayloads(sysid: string): Promise<CallPayload[]> {
    const { data } = await http.get<CallPayload[]>(apiUrl("/rest/status/listPayloads"), {
      params: { sysid },
      headers: BG,
    });
    return Array.isArray(data) ? data : [];
  },

  /** Fila de origem de uma transferência (por callid ou, sem callid, pelo início da chamada). */
  async getTransferredFrom(callid: string | null, callStartedAt: Date): Promise<TransferredFrom | null> {
    const params = callid ? { callid } : { callStartedAt: formatBackendDateTime(callStartedAt) };
    const { data } = await http.get<TransferredFrom | null>(apiUrl("/rest/status/getTransferredFrom"), {
      params,
      headers: BG,
    });
    return data && (data.name || data.queueNumber) ? data : null;
  },

  /** Log de chamada feita pelo ramal alternativo (paridade com o softphone legado). */
  async insertLogAlternativePhone(displayName: string, started: Date, ended: Date): Promise<void> {
    await http.post(
      apiUrl("/rest/log/insertLogAlternativePhone"),
      { displayName, started, ended },
      { headers: BG },
    );
  },
};
